import { Link } from 'react-router-dom';
import { 
  ScrollText, FileText, Users, Briefcase, 
  ArrowRight, Settings, Clock, CheckCircle
} from 'lucide-react';

const adminModules = [
  {
    id: 'contracts',
    title: 'Contract Documents',
    description: 'Create and manage contract templates for quotes and proposals',
    icon: ScrollText,
    path: '/crm/admin/contracts',
    color: 'bg-blue-500',
    bgLight: 'bg-blue-50',
    available: true
  },
  {
    id: 'quote-templates',
    title: 'Quote Templates',
    description: 'Pre-built quote templates for faster proposal creation',
    icon: FileText,
    path: '/crm/admin/quote-templates',
    color: 'bg-purple-500',
    bgLight: 'bg-purple-50',
    available: false
  },
  {
    id: 'team',
    title: 'Team Management',
    description: 'Manage team members, roles, and permissions',
    icon: Users,
    path: '/crm/admin/team',
    color: 'bg-green-500',
    bgLight: 'bg-green-50',
    available: false
  },
  {
    id: 'services',
    title: 'Service Catalog',
    description: 'Define services and pricing for your business',
    icon: Briefcase,
    path: '/crm/admin/services',
    color: 'bg-orange-500',
    bgLight: 'bg-orange-50',
    available: false
  }
];

export default function AdminDashboardPage() {
  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#0A1628]">Admin Dashboard</h1>
        <p className="text-gray-500 mt-1">Manage your business settings and configurations</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <ScrollText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A1628]">-</p>
              <p className="text-xs text-gray-500">Contract Templates</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A1628]">-</p>
              <p className="text-xs text-gray-500">Active Contracts</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A1628]">-</p>
              <p className="text-xs text-gray-500">Team Members</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A1628]">-</p>
              <p className="text-xs text-gray-500">Services</p>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Modules Grid */}
      <div>
        <h2 className="text-lg font-semibold text-[#0A1628] mb-4">Admin Modules</h2>
        <div className="grid grid-cols-2 gap-4">
          {adminModules.map((module) => (
            <Link
              key={module.id}
              to={module.available ? module.path : '#'}
              onClick={(e) => !module.available && e.preventDefault()}
              className={`group bg-white rounded-xl border border-gray-200 p-6 transition-all hover:shadow-md ${
                module.available ? 'hover:border-[#84CC16]/50 cursor-pointer' : 'opacity-60 cursor-not-allowed'
              }`}
              data-testid={`admin-module-${module.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 ${module.bgLight} rounded-xl flex items-center justify-center`}>
                    <module.icon className={`w-6 h-6 ${module.color.replace('bg-', 'text-').replace('-500', '-600')}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-[#0A1628]">{module.title}</h3>
                      {!module.available && (
                        <span className="text-[10px] bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">Coming Soon</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{module.description}</p>
                  </div>
                </div>
                {module.available && (
                  <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-[#84CC16] transition-colors" />
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity Placeholder */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-[#0A1628]">Recent Admin Activity</h2>
          <Clock className="w-5 h-5 text-gray-400" />
        </div>
        <div className="text-center py-8 text-gray-400">
          <Settings className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Activity tracking coming soon</p>
        </div>
      </div>
    </div>
  );
}
